import React from 'react';
import { MarketplaceEvalResponse, MPEResult, PriceEstimate } from '../types';

interface MPEResultsProps {
  response: MarketplaceEvalResponse;
  onClose: () => void;
}

const MPEResults: React.FC<MPEResultsProps> = ({ response, onClose }) => {
  const handleCardClick = (result: MPEResult) => {
    // Guard: Skip if URL is missing
    if (!result.url) {
      console.warn('MPE result missing URL:', result);
      return;
    }

    // Process URL to bypass interstitial pages
    let targetUrl = result.url;

    // Etsy: Add parameters to show full listing details (bypasses "sold out" interstitial)
    // Handles both etsy.com/listing/ and etsy.com/{country}/listing/ (e.g., etsy.com/au/listing/)
    if (targetUrl.includes('etsy.com') && targetUrl.includes('/listing/')) {
      const url = new URL(targetUrl);
      url.searchParams.set('show_sold_out_detail', '1');
      url.searchParams.set('ref', 'nla_listing_details');
      targetUrl = url.toString();
    }

    // Open in a controlled popup window
    const width = 1200;
    const height = 900;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    window.open(
      targetUrl,
      '_blank',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=yes,toolbar=yes,location=yes`
    );
  };

  return (
    <div style={{
      marginTop: '16px',
      padding: '12px',
      backgroundColor: 'var(--bg-secondary)',
      borderRadius: '8px',
      border: '1px solid var(--border-light)',
      maxWidth: '100%',
      boxSizing: 'border-box'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '12px',
        gap: '8px'
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{
            margin: 0,
            fontSize: 'clamp(1rem, 4vw, 1.25rem)',
            fontWeight: 600,
            color: 'var(--text-primary)',
            wordBreak: 'break-word'
          }}>
            Marketplace Results
          </h3>
          <p style={{
            margin: '4px 0 0 0',
            fontSize: 'clamp(0.75rem, 3.5vw, 0.875rem)',
            color: 'var(--text-secondary)',
            wordBreak: 'break-word'
          }}>
            Found {response.totalResults} similar items
            {response.cacheHit && ' (cached results)'}
          </p>
        </div>

        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '32px',
            cursor: 'pointer',
            color: 'var(--text-secondary)',
            lineHeight: 1,
            padding: 0,
            width: '32px',
            height: '32px',
            minWidth: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
          title="Close results"
        >
          ×
        </button>
      </div>

      {/* Search query info — only show when a search query was provided */}
      {response.searchQuery && response.searchQuery.trim() !== '' && (
        <div style={{
          marginBottom: '16px',
          padding: '10px',
          backgroundColor: 'var(--bg-card)',
          borderRadius: '6px',
          fontSize: 'clamp(0.75rem, 3.5vw, 0.875rem)',
          color: 'var(--text-primary)',
          wordWrap: 'break-word',
          overflowWrap: 'break-word',
          maxWidth: '100%'
        }}>
          <strong>Search:</strong> "{response.searchQuery}"
          {response.queryEdited && response.suggestedQuery && (
            <span style={{
              color: 'var(--text-secondary)',
              marginLeft: '8px',
              display: 'block',
              marginTop: '4px'
            }}>
              (edited from "{response.suggestedQuery}")
            </span>
          )}
        </div>
      )}

      {/* Price Estimate Card */}
      {response.priceEstimate && (
        <PriceEstimateCard estimate={response.priceEstimate} />
      )}

      {/* Results Grid */}
      {response.results.length > 0 ? (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))',
          gap: '12px',
          maxWidth: '100%'
        }}>
          {response.results.map((result) => (
            <MPEResultCard
              key={result.position}
              result={result}
              onClick={() => handleCardClick(result)}
            />
          ))}
        </div>
      ) : (
        <div style={{
          textAlign: 'center',
          padding: '20px 12px',
          backgroundColor: 'var(--bg-card)',
          borderRadius: '6px',
          color: 'var(--text-secondary)',
          fontSize: 'clamp(0.75rem, 3.5vw, 0.875rem)',
          maxWidth: '100%',
          wordWrap: 'break-word'
        }}>
          No marketplace listings found for this search.
          Try adjusting your search terms.
        </div>
      )}

    </div>
  );
};

interface PriceEstimateCardProps {
  estimate: PriceEstimate;
}

const PriceEstimateCard: React.FC<PriceEstimateCardProps> = ({ estimate }) => {
  const isNone = estimate.confidence === 'NONE' ||
    (estimate.lowPrice === 0 && estimate.highPrice === 0);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: estimate.currency || 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  return (
    <div style={{
      marginBottom: '16px',
      padding: '20px',
      backgroundColor: 'var(--bg-card)',
      border: '1px solid var(--border-light)',
      borderRadius: '8px'
    }}>
      <h4 style={{
        margin: '0 0 12px 0',
        fontSize: 'clamp(0.875rem, 3.5vw, 1rem)',
        fontWeight: 600,
        color: 'var(--text-primary)'
      }}>
        AI Resale Price Estimate
      </h4>

      {isNone ? (
        <p style={{
          margin: '0 0 8px 0',
          fontSize: 'clamp(0.8rem, 3.5vw, 0.9rem)',
          color: 'var(--text-secondary)',
          lineHeight: 1.6
        }}>
          Unable to estimate resale price.
          {estimate.rationale && (
            <span> {estimate.rationale}</span>
          )}
        </p>
      ) : (
        <>
          <p style={{
            margin: '0 0 12px 0',
            fontSize: 'clamp(1rem, 4vw, 1.25rem)',
            fontWeight: 600,
            color: 'var(--text-primary)'
          }}>
            Resell range: {formatPrice(estimate.lowPrice)} — {formatPrice(estimate.highPrice)}
            <span style={{
              fontWeight: 400,
              color: 'var(--text-secondary)',
              fontSize: 'clamp(0.75rem, 3vw, 0.875rem)',
              marginLeft: '8px'
            }}>
              ({estimate.confidence.toLowerCase()} confidence estimate)
            </span>
          </p>

          {estimate.rationale && (
            <p style={{
              margin: '0 0 12px 0',
              fontSize: 'clamp(0.75rem, 3.5vw, 0.875rem)',
              color: 'var(--text-primary)',
              lineHeight: 1.6
            }}>
              {estimate.rationale}
            </p>
          )}

        </>
      )}
    </div>
  );
};

interface MPEResultCardProps {
  result: MPEResult;
  onClick: () => void;
}

const MPEResultCard: React.FC<MPEResultCardProps> = ({ result, onClick }) => {
  return (
    <div
      onClick={onClick}
      style={{
        backgroundColor: 'var(--bg-card)',
        border: '1px solid var(--border-light)',
        borderRadius: '8px',
        padding: '10px',
        display: 'block',
        transition: 'box-shadow 0.2s, transform 0.2s',
        cursor: 'pointer',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      {/* Thumbnail */}
      <div style={{
        width: '100%',
        height: 'clamp(150px, 40vw, 200px)',
        overflow: 'hidden',
        borderRadius: '4px',
        marginBottom: '10px',
        backgroundColor: 'var(--bg-secondary)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {result.thumbnailUrl ? (
          <img
            src={result.thumbnailUrl}
            alt={result.title}
            loading="lazy"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              maxWidth: '100%'
            }}
          />
        ) : (
          <div style={{
            fontSize: '48px',
            color: 'var(--text-secondary)',
            opacity: 0.6
          }}>
            🏺
          </div>
        )}
      </div>

      {/* Content */}
      <div style={{ maxWidth: '100%', overflow: 'hidden' }}>
        <h4 style={{
          fontSize: 'clamp(12px, 3.5vw, 14px)',
          fontWeight: 500,
          margin: '0 0 6px 0',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          lineHeight: 1.4,
          color: 'var(--text-primary)',
          wordBreak: 'break-word',
          overflowWrap: 'break-word'
        }}>
          {result.title}
        </h4>

        <p style={{
          fontSize: 'clamp(11px, 3vw, 12px)',
          color: 'var(--text-secondary)',
          margin: '0 0 6px 0',
          display: '-webkit-box',
          WebkitLineClamp: 5,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          lineHeight: 1.5,
          wordBreak: 'break-word',
          overflowWrap: 'break-word'
        }}>
          {result.snippet}
        </p>

        {/* Bottom row: Marketplace name (left) and Source indicator (right) */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span style={{
            fontSize: 'clamp(11px, 3vw, 12px)',
            color: 'var(--text-accent)',
            fontWeight: 500,
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            flex: 1,
            minWidth: 0
          }}>
            {result.ogSiteName || result.displayLink}
          </span>

          {/* Source indicator badge: IMG or LENS */}
          {result.source && (
            <span
              style={{
                fontSize: '9px',
                fontWeight: 600,
                letterSpacing: '0.5px',
                color: result.source === 'GOOGLE_IMAGES' ? 'var(--primary-light)' : 'var(--success-dark)',
                backgroundColor: result.source === 'GOOGLE_IMAGES' ? 'var(--bg-accent)' : 'var(--success-bg)',
                padding: '2px 6px',
                borderRadius: '4px',
                flexShrink: 0,
                marginLeft: '6px',
                display: 'inline-block',
                textTransform: 'uppercase',
                border: `1px solid ${result.source === 'GOOGLE_IMAGES' ? 'var(--border-light)' : 'var(--success)'}`
              }}
              title={result.source === 'GOOGLE_IMAGES' ? 'From Google Images search' : 'From Google Lens search'}
            >
              {result.source === 'GOOGLE_IMAGES' ? 'IMG' : 'LENS'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default MPEResults;
